import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/contexts/LanguageContext";
import { format, formatDistanceToNow, differenceInMinutes } from "date-fns";
import { es, enUS } from "date-fns/locale";
import {
  Play,
  Pause,
  Clock,
  User,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ActiveProcess {
  id: string;
  status: string;
  started_at: string | null;
  notes: string | null;
  manufacturing_processes: {
    name: string;
    estimated_duration_minutes: number | null;
  };
  work_orders: {
    order_number: string;
    product_name: string;
  };
  profiles: {
    full_name: string | null;
    email: string;
  } | null;
}

export function ProcessMonitoringDashboard() {
  const { language } = useLanguage();
  const locale = language === "es" ? es : enUS;

  // Fetch active processes with real-time polling
  const { data: activeProcesses, isLoading } = useQuery({
    queryKey: ["active-processes-monitor"],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select(`
          id,
          status,
          started_at,
          notes,
          manufacturing_processes(name, estimated_duration_minutes),
          work_orders(order_number, product_name),
          profiles(full_name, email)
        `)
        .in("status", ["in_progress", "paused"])
        .order("started_at", { ascending: false });

      if (error) throw error;
      return data as ActiveProcess[];
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Calculate elapsed time and progress
  const getElapsedInfo = (process: ActiveProcess) => {
    if (!process.started_at) return { elapsed: 0, percentage: 0, isOvertime: false };

    const startTime = new Date(process.started_at);
    const elapsedMinutes = differenceInMinutes(new Date(), startTime);
    const estimatedMinutes = process.manufacturing_processes?.estimated_duration_minutes || 60;
    const percentage = Math.min(100, Math.round((elapsedMinutes / estimatedMinutes) * 100));
    const isOvertime = elapsedMinutes > estimatedMinutes;

    return { elapsed: elapsedMinutes, percentage, isOvertime };
  };

  const formatElapsed = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Group by status
  const inProgress = activeProcesses?.filter((p) => p.status === "in_progress") || [];
  const paused = activeProcesses?.filter((p) => p.status === "paused") || [];
  const overtime = inProgress.filter((p) => getElapsedInfo(p).isOvertime);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Activity className="w-6 h-6 animate-pulse text-primary" />
        <span className="ml-2 text-muted-foreground">Cargando procesos activos...</span>
      </div>
    );
  }

  if (!activeProcesses || activeProcesses.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
          <p className="text-lg font-medium">No hay procesos activos</p>
          <p className="text-sm text-muted-foreground">
            Todos los procesos están completados o pendientes de iniciar
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{inProgress.length}</p>
                <p className="text-sm text-muted-foreground">En Progreso</p>
              </div>
              <Play className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{paused.length}</p>
                <p className="text-sm text-muted-foreground">Pausados</p>
              </div>
              <Pause className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn("border-l-4", overtime.length > 0 ? "border-l-red-500" : "border-l-green-500")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{overtime.length}</p>
                <p className="text-sm text-muted-foreground">Excedidos</p>
              </div>
              <AlertTriangle className={cn("w-8 h-8", overtime.length > 0 ? "text-red-500" : "text-green-500")} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Processes Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {activeProcesses.map((process) => {
          const { elapsed, percentage, isOvertime } = getElapsedInfo(process);
          const estimated = process.manufacturing_processes?.estimated_duration_minutes || 0;

          return (
            <Card
              key={process.id}
              className={cn(
                "transition-all hover:shadow-lg",
                process.status === "paused" && "opacity-75",
                isOvertime && "ring-2 ring-red-500/50"
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {process.manufacturing_processes?.name || "Proceso"}
                      {process.status === "in_progress" ? (
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                      ) : (
                        <Pause className="w-4 h-4 text-orange-500" />
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {process.work_orders?.order_number}
                    </p>
                  </div>
                  <Badge
                    className={cn(
                      process.status === "in_progress"
                        ? "bg-blue-500"
                        : "bg-orange-500"
                    )}
                  >
                    {process.status === "in_progress" ? "En Progreso" : "Pausado"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Product Name */}
                <p className="text-sm font-medium truncate">
                  {process.work_orders?.product_name}
                </p>

                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className={cn("font-medium", isOvertime && "text-red-500")}>
                      {formatElapsed(elapsed)} / {estimated ? `${estimated}m est.` : "-"}
                    </span>
                  </div>
                  <Progress
                    value={percentage}
                    className={cn(
                      "h-2",
                      isOvertime && "[&>div]:bg-red-500"
                    )}
                  />
                  {isOvertime && (
                    <div className="flex items-center gap-1 text-xs text-red-500">
                      <AlertTriangle className="w-3 h-3" />
                      Excedido por {formatElapsed(elapsed - estimated)}
                    </div>
                  )}
                </div>

                {/* Operator & Time Info */}
                <div className="flex items-center justify-between text-sm">
                  {process.profiles && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span className="truncate max-w-[120px]">
                        {process.profiles.full_name || process.profiles.email}
                      </span>
                    </div>
                  )}
                  {process.started_at && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>
                        {formatDistanceToNow(new Date(process.started_at), {
                          locale,
                          addSuffix: false,
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Notes if any */}
                {process.notes && (
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded truncate">
                    {process.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
